"use client";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Printer, Download, FileText } from "lucide-react";

export interface PrintColumn {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
}

export interface PrintData {
  title: string;
  docNumber?: string;
  docDate?: string;
  company?: { name: string; address: string; phone: string; email: string };
  meta?: { label: string; value: string }[];
  columns: PrintColumn[];
  rows: Record<string, React.ReactNode | string | number>[];
  totals?: { label: string; value: string }[];
  signatures?: { label: string; name: string }[];
  notes?: string;
}

const DEFAULT_COMPANY = {
  name: "OMEGA Distribution Ltd.",
  address: "Level 7, Gulshan Avenue, Dhaka 1212, Bangladesh",
  phone: "+880 2 9876543",
  email: "info@omega-dist.bd",
};

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

export function buildPrintHTML(d: PrintData): string {
  const company = d.company ?? DEFAULT_COMPANY;
  const metaHtml = d.meta ? d.meta.map(m => `<div class="meta-row"><span class="meta-label">${esc(m.label)}</span><span class="meta-val">${esc(m.value)}</span></div>`).join("") : "";
  const head = d.columns.map(c => `<th style="text-align:${c.align || "left"}">${esc(c.label)}</th>`).join("");
  const body = d.rows.map(r => "<tr>" + d.columns.map(c => `<td style="text-align:${c.align || "left"}">${esc(r[c.key])}</td>`).join("") + "</tr>").join("");
  const totalsHtml = d.totals && d.totals.length ? `<tr class="totals-row"><td colspan="${d.columns.length - 1}" style="text-align:right;font-weight:600;">${d.totals[0].label}</td><td style="text-align:right;font-weight:700;">${esc(d.totals[0].value)}</td></tr>` + d.totals.slice(1).map(t => `<tr class="totals-row"><td colspan="${d.columns.length - 1}" style="text-align:right;font-weight:600;">${esc(t.label)}</td><td style="text-align:right;font-weight:700;">${esc(t.value)}</td></tr>`).join("") : "";
  const sigHtml = d.signatures && d.signatures.length ? `<div class="sign-grid">${d.signatures.map(s => `<div class="sign-block"><div class="sign-line"></div><div class="sign-name">${esc(s.name)}</div><div class="sign-label">${esc(s.label)}</div></div>`).join("")}</div>` : "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(d.title)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1f2937; font-size: 12px; margin: 0; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 14px; }
  .company-name { font-size: 20px; font-weight: 700; color: #111827; }
  .company-meta { color:#4b5563; font-size: 11px; margin-top: 4px; }
  .doc-title { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
  .doc-num { color:#4b5563; font-size: 11px; margin-top: 2px; }
  .meta-grid { display:grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin: 14px 0; background: #f9fafb; padding: 10px 14px; border-radius: 6px; }
  .meta-row { display:flex; justify-content: space-between; }
  .meta-label { color:#6b7280; font-size: 11px; }
  .meta-val { color:#111827; font-weight: 600; font-size: 11px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #111827; color: #fff; padding: 8px 10px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; }
  td { padding: 7px 10px; border-bottom: 1px solid #e5e7eb; }
  .totals-row td { border-top: 1px solid #111827; background: #f3f4f6; }
  .sign-grid { display:grid; grid-template-columns: repeat(${d.signatures?.length || 2}, 1fr); gap: 32px; margin-top: 60px; }
  .sign-block { text-align: center; }
  .sign-line { border-top: 1px solid #1f2937; margin-bottom: 4px; }
  .sign-name { font-weight: 600; font-size: 11px; }
  .sign-label { color:#6b7280; font-size: 10px; }
  .notes { margin-top: 20px; color:#6b7280; font-size: 11px; font-style: italic; }
  .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #e5e7eb; color:#9ca3af; font-size: 10px; text-align: center; }
</style></head>
<body>
  <div class="header">
    <div>
      <div class="company-name">${esc(company.name)}</div>
      <div class="company-meta">${esc(company.address)}<br/>Tel: ${esc(company.phone)} · ${esc(company.email)}</div>
    </div>
    <div style="text-align:right">
      <div class="doc-title">${esc(d.title)}</div>
      ${d.docNumber ? `<div class="doc-num">${esc(d.docNumber)}</div>` : ""}
      ${d.docDate ? `<div class="doc-num">Date: ${esc(d.docDate)}</div>` : ""}
    </div>
  </div>
  ${metaHtml ? `<div class="meta-grid">${metaHtml}</div>` : ""}
  <table><thead><tr>${head}</tr></thead><tbody>${body}${totalsHtml}</tbody></table>
  ${d.notes ? `<div class="notes">Notes: ${esc(d.notes)}</div>` : ""}
  ${sigHtml}
  <div class="footer">Generated by OMEGA Distribution ERP · ${new Date().toLocaleString("en-BD")}</div>
</body></html>`;
}

export function PrintButton({ data, label = "Print", size = "sm" }: { data: PrintData; label?: string; size?: "sm" | "default" }) {
  const onPrint = () => {
    const html = buildPrintHTML(data);
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { alert("Please allow pop-ups to print."); return; }
    w.document.open(); w.document.write(html); w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 400);
  };
  return <Button size={size} variant="outline" onClick={onPrint}><Printer className="size-4" /> {label}</Button>;
}

export function CSVExportButton<T extends object = Record<string, unknown>>({
  columns, rows, filename, label = "CSV", size = "sm"
}: {
  columns: { key: string; label: string }[];
  rows: readonly T[];
  filename?: string;
  label?: string;
  size?: "sm" | "default";
}) {
  const onExport = () => {
    const esc = (v: unknown) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map(c => esc(c.label)).join(",");
    const body = rows.map(r => columns.map(c => esc((r as Record<string, unknown>)[c.key])).join(",")).join("\n");
    const csv = "\ufeff" + header + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `export-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <Button size={size} variant="outline" onClick={onExport}><Download className="size-4" /> {label}</Button>;
}

export function PrintPreviewBadge({ docNumber }: { docNumber?: string }) {
  return docNumber ? (
    <div className="text-muted-foreground inline-flex items-center gap-1 text-xs">
      <FileText className="size-3" /> {docNumber}
    </div>
  ) : null;
}
